import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X as XIcon, Send, Loader2, Share2, Megaphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal for sharing a workout to friends.
 * Modes:
 *   - feed: post a workout_share card to the public Friends Feed
 *   - dm:   send as a DM attachment to selected friends
 *   - both: do both
 */
export default function ShareWorkoutDialog({ workout, onClose }) {
  const [mode, setMode] = useState("both");
  const [friends, setFriends] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/friends`);
        if (!cancelled) setFriends(data.friends || []);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if ((mode === "dm" || mode === "both") && selected.size === 0 && mode === "dm") {
      toast.error("Pick at least one friend to DM, or switch to Feed mode.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        mode,
        to_user_ids: Array.from(selected),
        workout: {
          workout_id: workout.workout_id,
          title: workout.title,
          tagline: workout.tagline,
          focus: workout.focus,
          level: workout.level,
          days_per_week: workout.days_per_week,
          duration_min: workout.duration_min,
          week_iso: workout.week_iso,
        },
        note,
      };
      const { data } = await axios.post(`${API}/workouts/share`, payload);
      const bits = [];
      if (data?.share_id) bits.push("posted to feed");
      const dmCount = (data?.conversation_ids || []).length;
      if (dmCount > 0) bits.push(`sent to ${dmCount} friend${dmCount > 1 ? "s" : ""}`);
      toast.success(`📤 Shared — ${bits.join(" · ") || "done"}!`, { duration: 4500 });
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="share-workout-modal"
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              Share Workout
            </div>
            <h3 className="font-display text-2xl text-white leading-tight">
              {workout?.title || "Workout"}
            </h3>
          </div>
          <button
            data-testid="share-close"
            onClick={onClose}
            className="text-neutral-500 hover:text-white"
          >
            <XIcon size={22} />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          <button
            data-testid="share-mode-feed"
            onClick={() => setMode("feed")}
            className={`py-2.5 border-2 font-bold uppercase tracking-widest text-[10px] transition-colors flex items-center justify-center gap-1 ${
              mode === "feed"
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
            }`}
          >
            <Megaphone size={11} /> Feed
          </button>
          <button
            data-testid="share-mode-dm"
            onClick={() => setMode("dm")}
            className={`py-2.5 border-2 font-bold uppercase tracking-widest text-[10px] transition-colors flex items-center justify-center gap-1 ${
              mode === "dm"
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
            }`}
          >
            <Send size={11} /> DM
          </button>
          <button
            data-testid="share-mode-both"
            onClick={() => setMode("both")}
            className={`py-2.5 border-2 font-bold uppercase tracking-widest text-[10px] transition-colors flex items-center justify-center gap-1 ${
              mode === "both"
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
            }`}
          >
            <Share2 size={11} /> Both
          </button>
        </div>

        <p className="text-[11px] text-neutral-500 mb-3">
          {mode === "feed"
            ? "Your workout share will appear in the public Friends Feed."
            : mode === "dm"
            ? "Pick friends to DM the workout to. They get a notification."
            : "Drops on the Feed and DMs the friends you pick below."}
        </p>

        {(mode === "dm" || mode === "both") && (
          <>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
              Send to ({selected.size})
            </div>
            {loading ? (
              <div className="text-center text-neutral-500 py-4 text-sm">
                <Loader2 className="animate-spin inline mr-2" size={14} />
                Loading friends…
              </div>
            ) : friends.length === 0 ? (
              <p className="text-xs text-neutral-500 py-3 italic">
                You have no friends yet. Use Feed mode or add friends first.
              </p>
            ) : (
              <div className="space-y-1.5 mb-3 max-h-[28vh] overflow-y-auto pr-1">
                {friends.map((f) => (
                  <button
                    key={f.user_id}
                    data-testid={`share-friend-${f.user_id}`}
                    onClick={() => toggle(f.user_id)}
                    className={`w-full flex items-center gap-3 p-2 border ${
                      selected.has(f.user_id)
                        ? "border-[#D4AF37] bg-[#D4AF37]/10"
                        : "border-zinc-800 hover:border-zinc-600"
                    }`}
                  >
                    {f.picture ? (
                      <img
                        src={f.picture}
                        alt={f.name}
                        className="w-8 h-8 object-contain bg-black border border-[#D4AF37]"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-[#4C1D95] border border-[#D4AF37] flex items-center justify-center font-display text-sm text-[#D4AF37]">
                        {(f.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="flex-1 text-left text-sm text-white truncate">
                      {f.full_name || f.name}
                    </span>
                    {selected.has(f.user_id) && (
                      <span className="text-[#D4AF37] text-xs font-bold">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <textarea
              data-testid="share-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a quick note (optional)…"
              maxLength={500}
              className="input-omni w-full min-h-[60px] mb-3"
            />
          </>
        )}

        <button
          data-testid="share-submit"
          onClick={submit}
          disabled={busy}
          className="btn-omni btn-red w-full disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
          Share Workout
        </button>
      </div>
    </div>
  );
}
